import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Check, Save, Loader2, CheckCircle2, FileText, BookOpen, Paperclip, Download } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import TopBar from "@/components/TopBar";

const emptyApp = (fields) => Object.fromEntries(fields.map((f) => [f.id, ""]));

export default function Survey() {
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [apps, setApps] = useState([]);
  const [status, setStatus] = useState("draft");
  const [active, setActive] = useState("Intro");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const dirty = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: sc }, { data: resp }] = await Promise.all([
          api.get("/survey/schema"),
          api.get("/survey/response"),
        ]);
        setSchema(sc);
        setAnswers(resp.answers || {});
        const cSection = sc.sections.find((s) => s.type === "repeatable");
        setApps((resp.apps && resp.apps.length ? resp.apps : [emptyApp(cSection.fields)]));
        setStatus(resp.status || "draft");
      } catch (e) {
        toast.error(apiErr(e));
      }
    })();
  }, []);

  const cFields = useMemo(
    () => schema?.sections.find((s) => s.type === "repeatable")?.fields || [],
    [schema]
  );

  const doSave = async (silent = true) => {
    if (status === "submitted") return;
    setSaving(true);
    try {
      await api.put("/survey/response", { answers, apps });
      dirty.current = false;
      setSavedAt(new Date());
      if (!silent) toast.success("Draft saved.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  // debounced autosave
  useEffect(() => {
    if (!schema || status === "submitted") return;
    if (!dirty.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => doSave(true), 1500);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [answers, apps]);

  const setAnswer = (id, val) => {
    dirty.current = true;
    setAnswers((a) => ({ ...a, [id]: val }));
  };
  const setAppField = (i, id, val) => {
    dirty.current = true;
    setApps((prev) => prev.map((a, idx) => (idx === i ? { ...a, [id]: val } : a)));
  };
  const addApp = () => { dirty.current = true; setApps((p) => [...p, emptyApp(cFields)]); };
  const removeApp = (i) => { dirty.current = true; setApps((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p)); };

  const progress = useMemo(() => {
    if (!schema) return { done: 0, total: 0 };
    let done = 0, total = 0;
    for (const s of schema.sections) {
      if (s.type === "repeatable") {
        total += 1;
        if (apps.some((a) => cFields.some((f) => (a[f.id] || "").trim()))) done += 1;
      } else if (s.questions) {
        for (const q of s.questions) {
          total += 1;
          if ((answers[q.id] || "").trim()) done += 1;
        }
      }
    }
    return { done, total };
  }, [schema, answers, apps, cFields]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/survey/response/submit", { answers, apps });
      setStatus("submitted");
      toast.success("Response submitted. Thank you!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!schema) {
    return (
      <div className="min-h-screen">
        <TopBar />
        <div className="flex items-center justify-center py-32 text-neutral-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </div>
    );
  }

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const activeSection = schema.sections.find((s) => s.id === active);
  const locked = status === "submitted";

  return (
    <div className="min-h-screen pb-24">
      <TopBar
        right={
          <div className="hidden lg:flex items-center gap-2 text-sm text-white/80">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : savedAt ? <Check className="w-4 h-4 text-amber-200" /> : null}
            {saving ? "Saving…" : savedAt ? "Saved" : ""}
          </div>
        }
      />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8">
        {locked && (
          <div className="card-surface p-5 mb-6 flex items-start gap-3 border-l-4" style={{ borderLeftColor: "var(--gold)" }} data-testid="submitted-banner">
            <CheckCircle2 className="w-6 h-6 text-green-700 shrink-0" />
            <div>
              <p className="font-semibold" style={{ color: "var(--burgundy)" }}>Your response has been submitted</p>
              <p className="text-sm text-neutral-600">Thank you. Your answers are now read-only. Contact your IAM lead if a change is needed.</p>
            </div>
          </div>
        )}

        {/* progress header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "var(--burgundy)" }}>Profiling Questionnaire</h1>
            <p className="text-neutral-500 mt-1 text-sm max-w-2xl">
              Answer from real, recent experience. Estimates are welcome — just flag them. Your progress saves automatically.
            </p>
          </div>
          <div className="min-w-[180px]">
            <div className="flex justify-between text-xs text-neutral-500 mb-1">
              <span>Progress</span><span>{progress.done}/{progress.total}</span>
            </div>
            <div className="h-2 rounded-full bg-[var(--muted)] overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: "var(--gold)" }} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
          {/* nav */}
          <nav className="lg:sticky lg:top-6 self-start space-y-1" data-testid="section-nav">
            {schema.sections.map((s) => (
              <button
                key={s.id}
                data-testid={`nav-section-${s.id}`}
                onClick={() => setActive(s.id)}
                className={`nav-pill ${active === s.id ? "active" : ""}`}
              >
                <span className="section-badge !w-7 !h-7 text-sm shrink-0">
                  {s.type === "intro" ? <BookOpen className="w-4 h-4" /> : s.type === "artifacts" ? <Paperclip className="w-4 h-4" /> : s.id}
                </span>
                <span className="truncate">{s.title}</span>
              </button>
            ))}
          </nav>

          {/* section content */}
          <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <div className="card-surface p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="section-badge">
                  {activeSection.type === "intro" ? <BookOpen className="w-5 h-5" /> : activeSection.type === "artifacts" ? <Paperclip className="w-5 h-5" /> : activeSection.id}
                </span>
                <h2 className="text-xl font-bold" style={{ color: "var(--burgundy)" }}>{activeSection.title}</h2>
              </div>

              {activeSection.type === "intro" ? (
                <div className="space-y-6" data-testid="intro-block">
                  {activeSection.purpose.map((p, i) => (
                    <p key={i} className="text-[15px] leading-relaxed text-neutral-700">{p}</p>
                  ))}
                  <div>
                    <p className="font-semibold text-sm mb-2" style={{ color: "var(--burgundy)" }}>How the sections are organised</p>
                    <ul className="space-y-1.5">
                      {activeSection.organised.map((o, i) => (
                        <li key={i} className="text-sm text-neutral-600 flex gap-2"><span style={{ color: "var(--gold)" }}>•</span>{o}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-lg p-4 bg-[#f6f1e6] border-l-4" style={{ borderLeftColor: "var(--gold)" }}>
                    <p className="font-semibold text-sm mb-2" style={{ color: "var(--burgundy)" }}>Notes</p>
                    <ul className="space-y-2">
                      {activeSection.notes.map((nt, i) => (
                        <li key={i} className="text-sm text-neutral-600">
                          <span className="font-semibold text-neutral-800">{nt.title}: </span>{nt.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button className="btn btn-primary" onClick={() => setActive("A")} data-testid="intro-start-btn">Start with Section A</button>
                </div>
              ) : activeSection.type === "artifacts" ? (
                <div className="space-y-5" data-testid="artifacts-block">
                  <p className="text-[15px] text-neutral-700">{activeSection.intro}</p>
                  <a href={activeSection.download} download className="btn btn-gold w-fit" data-testid="artifacts-download-btn">
                    <Download className="w-4 h-4" /> Download artifacts request list
                  </a>
                  <ul className="space-y-3 mt-2">
                    {activeSection.items.map((it, i) => (
                      <li key={i} className="rounded-lg border border-[hsl(var(--border))] p-4">
                        <p className="font-semibold text-sm mb-2" style={{ color: "var(--burgundy-2)" }}>{i + 1}. {it.title}</p>
                        <div className="space-y-2">
                          {it.content.map((c, j) => {
                            if (c.kind === "list") {
                              return (
                                <ul key={j} className="ml-2 space-y-1">
                                  {c.items.map((x, k) => (
                                    <li key={k} className="text-sm text-neutral-600 flex gap-2"><span style={{ color: "var(--gold)" }}>•</span>{x}</li>
                                  ))}
                                </ul>
                              );
                            }
                            if (c.kind === "def") {
                              return (
                                <p key={j} className="text-sm text-neutral-600">
                                  <span className="font-semibold text-neutral-800">{c.label} </span>{c.text}
                                </p>
                              );
                            }
                            return <p key={j} className="text-sm text-neutral-600">{c.text}</p>;
                          })}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : activeSection.type === "repeatable" ? (
                <div className="space-y-6">
                  <p className="text-sm text-neutral-600 bg-[#f6f1e6] border-l-4 p-3 rounded" style={{ borderLeftColor: "var(--gold)" }}>
                    {activeSection.note}
                  </p>
                  {apps.map((appVal, i) => (
                    <div key={i} className="rounded-lg border border-[hsl(var(--border))] p-5 relative" data-testid={`app-block-${i}`}>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold" style={{ color: "var(--burgundy-2)" }}>Application {i + 1}</span>
                        {!locked && apps.length > 1 && (
                          <button data-testid={`remove-app-${i}`} onClick={() => removeApp(i)} className="text-neutral-400 hover:text-red-600 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {cFields.map((f) => (
                          <div key={f.id} className={f.type === "textarea" || f.id === "steps" ? "md:col-span-2" : ""}>
                            <label className="block text-sm font-medium mb-1">{f.label}</label>
                            {f.type === "textarea" ? (
                              <textarea disabled={locked} rows={3} className="field-input" value={appVal[f.id] || ""} placeholder={f.hint || ""} onChange={(e) => setAppField(i, f.id, e.target.value)} data-testid={`app-${i}-${f.id}`} />
                            ) : (
                              <input disabled={locked} className="field-input" value={appVal[f.id] || ""} placeholder={f.hint || ""} onChange={(e) => setAppField(i, f.id, e.target.value)} data-testid={`app-${i}-${f.id}`} />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!locked && (
                    <button data-testid="add-app-btn" onClick={addApp} className="btn btn-outline">
                      <Plus className="w-4 h-4" /> Add another application
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {activeSection.questions.map((q, idx) => (
                    <div key={q.id}>
                      <label className="block text-sm font-medium mb-1.5">
                        <span className="text-[var(--burgundy-2)] font-semibold mr-1">{idx + 1}.</span>{q.label}
                      </label>
                      <textarea disabled={locked} rows={3} className="field-input" value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} data-testid={`q-${q.id}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* actions */}
            {!locked && activeSection.type !== "intro" && activeSection.type !== "artifacts" && (
              <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                <button data-testid="save-draft-btn" onClick={() => doSave(false)} disabled={saving} className="btn btn-outline">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
                </button>
                <button data-testid="submit-btn" onClick={submit} disabled={submitting} className="btn btn-primary">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Submit response
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
